import type { Branding } from '@/shared/branding/branding.types';

/**
 * Applies branding to the live document.
 *
 * Tailwind 4 emits the `@theme` tokens once on `:root` and every utility references them
 * through `var()`, so overriding `--color-brand-*` here changes every `bg-brand-500` in
 * the application without recompiling. That fact was verified against the built bundle
 * before this module was written; if it ever stops being true, this stops working and the
 * failure would be silent.
 */

const STYLE_ELEMENT_ID = 'branding';

export const BRANDING_STORAGE_KEY = 'rrhh.branding';

/**
 * The value is interpolated into a stylesheet. It only ever comes from the API, and the
 * API only ever derives it — but a value crossing into CSS is checked at the boundary
 * rather than trusted because of where it came from.
 */
const HEX = /^#[0-9a-f]{6}$/i;

export function applyBranding(branding: Branding): void {
  const declarations = Object.entries(branding.palette)
    .filter(([step, hex]) => /^\d+$/.test(step) && HEX.test(hex))
    .map(([step, hex]) => `--color-brand-${step}:${hex}`)
    .join(';');

  style().textContent = `:root{${declarations}}`;

  siteName = branding.siteName;
  composeTitle();

  applyFavicon(branding.logoUrl);
}

/**
 * The document title has two authors: the router, which knows the page, and branding,
 * which knows the site. Composing it in one place is what stops the second one from
 * arriving late and overwriting the first.
 */
let siteName = 'Talento';
let pageTitle = '';

export function setPageTitle(title: string): void {
  pageTitle = title;
  composeTitle();
}

function composeTitle(): void {
  // Assigned as text, never as markup, so a site name is only ever a site name.
  document.title = pageTitle ? `${pageTitle} · ${siteName}` : siteName;
}

/**
 * One element, replaced in place. Appending a second would leave the browser resolving
 * whichever came last — a race nobody would think to look for.
 */
function style(): HTMLStyleElement {
  const existing = document.querySelector<HTMLStyleElement>(`style#${STYLE_ELEMENT_ID}`);

  if (existing) {
    return existing;
  }

  const created = document.createElement('style');
  created.id = STYLE_ELEMENT_ID;
  document.head.append(created);

  return created;
}

function applyFavicon(logoUrl: string | null): void {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

  if (logoUrl === null) {
    // Removed rather than left pointing at a URL that now answers 404.
    existing?.remove();
    return;
  }

  const link = existing ?? document.createElement('link');
  link.rel = 'icon';
  link.href = logoUrl;

  if (!existing) {
    document.head.append(link);
  }
}

/**
 * The cache exists for one reason: applied synchronously before mount, it removes the
 * flash where the default brass paints first and then snaps to the real colour.
 */
export function cacheBranding(branding: Branding): void {
  try {
    localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(branding));
  } catch {
    // Private browsing, a full quota, storage disabled by policy. The cache is an
    // optimisation; losing it costs one frame of default theme, not correctness.
  }
}

/**
 * Storage is writable by anything running on this origin and survives deploys, so what
 * comes out of it is untrusted input rather than something this application wrote.
 */
export function cachedBranding(): Branding | null {
  try {
    const raw = localStorage.getItem(BRANDING_STORAGE_KEY);

    if (raw === null) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);

    return isBranding(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function isBranding(value: unknown): value is Branding {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Record<string, unknown>;

  return (
    typeof candidate.siteName === 'string' &&
    typeof candidate.brandColor === 'string' &&
    typeof candidate.palette === 'object' &&
    candidate.palette !== null &&
    (candidate.logoUrl === null || typeof candidate.logoUrl === 'string')
  );
}
